import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Badge,
    Modal, Nav, NavItem, NavLink, TabContent, TabPane, Row, Col, InputGroup, Input
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { Eye, User, Lock, Unlock, Trash2 } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";

interface PatientData {
    id: number;
    name: string;
    dob: string;
    phone_number: string;
    email: string | null;
    address: string | null;
    profile_pic: string | null;
    terms_accepted: boolean;
    privacy_accepted: boolean;
    is_verified: boolean;
    is_enable: boolean;
    created_at: string;
}

export default function ViewPatients() {
    const [data, setData] = useState<PatientData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("id");
    const [sortDir, setSortDir] = useState("desc");
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewModal, setViewModal] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState<PatientData | null>(null);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    const formatDateToShow = (dateStr: string | null) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear().toString().slice(-2);
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchData(currentPage, perPage, sortBy, sortDir, searchTerm);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, refresh, currentPage, perPage, sortBy, sortDir]);

    const fetchData = async (page: number, size: number, sort: string, direction: string, search: string) => {
        setLoading(true);
        try {
            const response: any = await apiPost(`patient/get_all?page=${page}`, {
                per_page: size,
                sort_by: sort,
                sort_direction: direction,
                search: search
            });
            setData(response.data || []);
            setTotalRows(response.total || 0);
        } catch (error) {
            toast.error("Failed to load patients");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || column.selector.toString().split('.').pop());
        setSortDir(sortDirection);
    };

    const handleView = (patient: PatientData) => {
        setSelectedPatient(patient);
        setViewModal(true);
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`patient/delete`, { id });
                    toast.success("Patient deleted successfully");
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete patient");
                }
            }
        });
    };

    const handleToggleEnable = async (id: number, currentStatus: boolean) => {
        const action = currentStatus ? 'Disable' : 'Enable';
        Swal.fire({
            title: `${action} Patient Account?`,
            text: `Are you sure you want to ${action.toLowerCase()} this patient?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: currentStatus ? '#d33' : '#3085d6',
            confirmButtonText: `Yes, ${action} it!`
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`patient/toggleEnable`, { id });
                    toast.success(`Patient account ${action.toLowerCase()}d successfully`);
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error(`Failed to ${action.toLowerCase()} patient account`);
                }
            }
        });
    };

    const columns: any[] = [
        {
            name: <div><span className="fw-bold">Sl.No</span></div>,
            selector: (row: PatientData, index: number) => (currentPage - 1) * perPage + index + 1,
            width: "70px"
        },
        { name: <div><span className="fw-bold">Name</span></div>, selector: (row: PatientData) => row.name, sortable: true, sortField: "name", wrap: true },
        { name: <div><span className="fw-bold">Phone</span></div>, selector: (row: PatientData) => row.phone_number, sortable: true, sortField: "phone_number" },
        { name: <div><span className="fw-bold">Email</span></div>, selector: (row: PatientData) => row.email || "N/A", sortable: true, sortField: "email", wrap: true },
        { name: <div><span className="fw-bold">DOB</span></div>, selector: (row: PatientData) => formatDateToShow(row.dob), sortable: true, sortField: "dob" },
        {
            name: <div><span className="fw-bold">Status</span></div>,
            sortable: true,
            sortField: "is_enable",
            center: true,
            cell: (row: PatientData) => (
                <Badge color={row.is_enable ? 'success' : 'danger'} pill>
                    {row.is_enable ? 'Enabled' : 'Disabled'}
                </Badge>
            ),
        },
        {
            name: <div><span className="fw-bold">Actions</span></div>,
            width: "180px",
            cell: (row: PatientData) => (
                <div className="d-flex gap-2">
                    <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Detail">
                        <Eye size={14} />
                    </Button>
                    {hasPermission('patient-management.enable') && (
                        <Button
                            size="sm"
                            color={row.is_enable ? "warning" : "success"}
                            onClick={() => handleToggleEnable(row.id, row.is_enable)}
                            title={row.is_enable ? "Disable" : "Enable"}
                        >
                            {row.is_enable ? <Lock size={14} /> : <Unlock size={14} />}
                        </Button>
                    )}
                    {hasPermission('patient-management.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Patient Management" parent="General" title="Patients" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <H3 className="text-primary mb-0">Patient Directory</H3>
                        </div>

                        <Row className="mb-3 justify-content-end">
                            <Col md={4} lg={3}>
                                <InputGroup className="shadow-sm border-0">
                                    <Input
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-control-sm"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <DataTable
                            columns={columns}
                            data={data}
                            progressPending={loading}
                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            sortServer
                            onSort={handleSort}
                            highlightOnHover
                            customStyles={customStyles}
                            noDataComponent={
                                <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                    <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                        <CardBody>
                                            <h6 className="fw-semibold mb-1">No Patients Found</h6>
                                            <small className="text-muted">No records matching your criteria.</small>
                                        </CardBody>
                                    </Card>
                                </div>
                            }
                        />
                    </CardBody>
                </Card>
            </Container>

            {/* Patient Detail Modal */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(!viewModal)} size="lg" centered>
                {selectedPatient && (
                    <div style={{
                        background: 'linear-gradient(135deg, #1e6ec8 0%, #0d4fa0 100%)',
                        padding: '1.5rem',
                        position: 'relative',
                        width: '100%'
                    }}>
                        <button
                            className="btn-close btn-close-white"
                            type="button"
                            onClick={() => setViewModal(false)}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '70px', height: '70px', borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.95)',
                                display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0,
                                padding: '4px', overflow: 'hidden'
                            }}>
                                {selectedPatient.profile_pic ? (
                                    <img src={`${mediaUrl}${selectedPatient.profile_pic}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                ) : (
                                    <User size={30} color="#1e6ec8" />
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{ color: 'white', fontWeight: 700, fontSize: '1.4rem', margin: 0, marginBottom: '0.25rem' }}>
                                    {selectedPatient.name}
                                </h3>
                                <div style={{ display: 'flex', gap: '0.75rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span>{selectedPatient.email || 'No Email'}</span>
                                    <span>•</span>
                                    <span style={{ fontWeight: 600 }}>{selectedPatient.phone_number}</span>
                                </div>
                            </div>
                            <div className="ms-auto pe-4">
                                <Badge color={selectedPatient.is_enable ? 'success' : 'danger'} pill style={{ padding: '8px 16px', fontSize: '0.8rem' }}>
                                    {selectedPatient.is_enable ? 'Active Account' : 'Disabled Account'}
                                </Badge>
                            </div>
                        </div>
                    </div>
                )}

                <div className="modal-body p-4 bg-light">
                    {selectedPatient && (
                        <div className="bg-white p-4 rounded shadow-sm">
                            <Row className="g-4">
                                <Col md={6}>
                                    <label className="text-muted small fw-bold text-uppercase">Date of Birth</label>
                                    <div className="fw-semibold fs-6 text-dark">{formatDateToShow(selectedPatient.dob)}</div>
                                </Col>
                                <Col md={6}>
                                    <label className="text-muted small fw-bold text-uppercase">Phone Number</label>
                                    <div className="fw-semibold fs-6 text-dark">{selectedPatient.phone_number}</div>
                                </Col>
                                <Col md={6}>
                                    <label className="text-muted small fw-bold text-uppercase">Email</label>
                                    <div className="fw-semibold fs-6 text-primary">{selectedPatient.email || 'No Email'}</div>
                                </Col>
                                <Col md={6}>
                                    <label className="text-muted small fw-bold text-uppercase">Terms Accepted</label>
                                    <div className={`fw-bold fs-6 ${selectedPatient.terms_accepted ? 'text-success' : 'text-danger'}`}>
                                        {selectedPatient.terms_accepted ? <><i className="fa fa-check-circle me-1"></i> Accepted</> : <><i className="fa fa-times-circle me-1"></i> Not Accepted</>}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <label className="text-muted small fw-bold text-uppercase">Privacy Policy</label>
                                    <div className={`fw-bold fs-6 ${selectedPatient.privacy_accepted ? 'text-success' : 'text-danger'}`}>
                                        {selectedPatient.privacy_accepted ? <><i className="fa fa-check-circle me-1"></i> Accepted</> : <><i className="fa fa-times-circle me-1"></i> Not Accepted</>}
                                    </div>
                                </Col>
                                <Col md={12}>
                                    <label className="text-muted small fw-bold text-uppercase">Full Address</label>
                                    <div className="p-3 bg-light rounded mt-1 text-dark" style={{ lineHeight: '1.6' }}>{selectedPatient.address || 'Address not provided'}</div>
                                </Col>
                                <Col md={12}>
                                    <label className="text-muted small fw-bold text-uppercase">Registration Date</label>
                                    <div className="text-muted small">{new Date(selectedPatient.created_at).toLocaleString()}</div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </div>
            </Modal>
            <ToastContainer />
        </div>
    );
}
